import { checkDeadlinePassed } from '../../components/special/checkDeadlinePassed';

// the tasks of a work package are patched and deleted on this path
export const TASK_PATH = 'work-packages/elements/list/tasks';

// the employees with their capacity, filtered by the API
export const CAPACITIES_PATH = 'users/capacities';

// the project picker lists the running projects of the whole company
export const PICKER_PROJECTS_PER_PAGE = 200;

// search and the pickers go to the capacities request, hideDone filters the
// listed tasks client side
export const emptyCapacityFilter = {
  search: '',
  userIds: [],
  projectIds: [],
  hideDone: true,
};

// users/capacities?search=example&projectId[]=5&userId[]=7
export function buildCapacitiesQuery({ search, userIds, projectIds } = {}) {
  const parts = [];

  if (search?.trim()) parts.push(`search=${encodeURIComponent(search.trim())}`);

  // the API repeats the key instead of taking a list
  (projectIds || []).forEach((id) => parts.push(`projectId[]=${id}`));
  (userIds || []).forEach((id) => parts.push(`userId[]=${id}`));

  return parts.join('&');
}

// the projects of the employee, each with their stats and their tasks
export function buildEmployeeProjectsPath(userId, projectIds = []) {
  const parts = ['withStatistics=true', 'withTasks=true'];

  projectIds.forEach((id) => parts.push(`projectId[]=${id}`));

  return `users/${userId}/projects?${parts.join('&')}`;
}

export const employeeName = (user) =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

// a capacity entry either is the employee or carries them
export const capacityUser = (entry) => entry?.user || entry;

// the counters the capacities request delivers per employee
export function employeeCounters(entry) {
  const stats = entry?.taskStats || capacityUser(entry)?.taskStats || entry || {};

  return {
    open: stats.openTaskCount || 0,
    points: stats.openTaskEffortScore || 0,
    projects: stats.projectCount || 0,
  };
}

// what the filtered employees add up to
export function capacityTotals(entries = []) {
  return entries.reduce(
    (sum, entry) => {
      const counters = employeeCounters(entry);

      return {
        open: sum.open + counters.open,
        points: sum.points + counters.points,
        employees: sum.employees + 1,
      };
    },
    { open: 0, points: 0, employees: 0 },
  );
}

/**
 * Every employee with their value for one metric, biggest first. Employees
 * without any are kept - an empty bar is exactly the free capacity a manager
 * is looking for.
 */
export function capacityRanking(entries = [], metric) {
  return entries
    .map((entry) => {
      const user = capacityUser(entry);

      return {
        id: user.id,
        label: employeeName(user),
        value: employeeCounters(entry)[metric],
      };
    })
    .sort((a, b) => b.value - a.value);
}

// the counters of an employee follow a changed task without a reload
export function applyEntryTaskChange(payload, userId, before, after) {
  const wasOpen = Boolean(before) && !before.status;
  const isOpen = Boolean(after) && !after.status;

  const open = (isOpen ? 1 : 0) - (wasOpen ? 1 : 0);
  const points =
    (isOpen ? after.effortScore || 0 : 0) -
    (wasOpen ? before.effortScore || 0 : 0);

  if (!payload || (!open && !points)) return payload;

  return {
    ...payload,
    data: (payload.data || []).map((entry) => {
      if (capacityUser(entry).id !== userId) return entry;

      const target = entry.taskStats || entry;
      const updated = {
        ...target,
        openTaskCount: (target.openTaskCount || 0) + open,
        openTaskEffortScore: (target.openTaskEffortScore || 0) + points,
      };

      return entry.taskStats ? { ...entry, taskStats: updated } : updated;
    }),
  };
}

// counters of the tasks a project brought with it
export function taskListStats(tasks = []) {
  const open = tasks.filter((task) => !task.status);

  return {
    total: tasks.length,
    done: tasks.length - open.length,
    open: open.length,
    overdue: open.filter(
      (task) => task.deadline && checkDeadlinePassed(task.deadline),
    ).length,
    openScore: open.reduce((sum, task) => sum + (task.effortScore || 0), 0),
  };
}

// withStatistics delivers { openTaskCount, openTaskEffortScore } per project
export function openTaskStats(project) {
  const stats = project?.taskStats;
  const fromTasks = taskListStats(project?.tasks);

  return {
    open: stats?.openTaskCount ?? fromTasks.open,
    points: stats?.openTaskEffortScore ?? fromTasks.openScore,
    // the payload has no overdue counter, so the listed tasks give it
    overdue: fromTasks.overdue,
  };
}

// the counters of a project row
export function projectHeaderStats(project, t, colors) {
  const stats = openTaskStats(project);

  return [
    { key: 'open', label: t('Open'), value: stats.open },
    {
      key: 'overdue',
      label: t('Overdue'),
      value: stats.overdue,
      color: stats.overdue ? colors.error : null,
    },
    { key: 'points', label: t('Points'), value: stats.points },
  ];
}

// a changed task is written back into the cached project, counters included.
// A null task removes it, which is what a delete leaves behind.
export function applyTaskChange(projects = [], projectId, taskId, task) {
  return projects.map((project) => {
    if (project.id !== projectId) return project;

    const tasks = project.tasks || [];
    const before = tasks.find((item) => item.id === taskId);

    const wasOpen = Boolean(before) && !before.status;
    const isOpen = Boolean(task) && !task.status;

    return {
      ...project,
      tasks: task
        ? tasks.map((item) => (item.id === taskId ? task : item))
        : tasks.filter((item) => item.id !== taskId),
      taskStats: project.taskStats && {
        ...project.taskStats,
        openTaskCount:
          (project.taskStats.openTaskCount || 0) +
          (isOpen ? 1 : 0) -
          (wasOpen ? 1 : 0),
        openTaskEffortScore:
          (project.taskStats.openTaskEffortScore || 0) +
          (isOpen ? task.effortScore || 0 : 0) -
          (wasOpen ? before.effortScore || 0 : 0),
      },
    };
  });
}

// the counters of an employee row, delivered with the capacities
export function employeeHeaderStats(entry, t) {
  const counters = employeeCounters(entry);

  return [
    { key: 'open', label: t('Open'), value: counters.open },
    { key: 'points', label: t('Points'), value: counters.points },
    { key: 'projects', label: t('Projects'), value: counters.projects },
  ];
}
